/**
 * 将 TikTok Get Attributes / 后端 publish/attrs 结果转换为发品 Editor（SHEIN 布局）可解析的 attribute_infos 形态。
 *
 * SHEIN 约定（parseAttributeTemplate）：
 * - attribute_type 1 = 销售属性，3/4 = 货品属性，2 = 尺码测量
 * - attribute_label 1 = 主销售（颜色）
 * - attribute_status 3 = 必填
 * - attribute_mode 0 手输 / 1 多选 / 3 单选 / 4 成分比例
 */

function mapControlTypeToMode(controlType, chooseMaxNum) {
  const ct = Number(controlType);
  // INPUT
  if (ct === 0) return 0;
  // PROPERTY_CHOOSE_AND_INPUT / 成分类
  if (ct === 16) return 4;
  // CHOOSE / INPUT_CHOOSE
  if (ct === 1 || ct === 3) {
    const max = Number(chooseMaxNum);
    if (Number.isFinite(max) && max > 1) return 1;
    return 3;
  }
  return 3;
}

function isEditorShape(attrsResult) {
  if (!attrsResult || typeof attrsResult !== 'object') return false;
  if (Array.isArray(attrsResult.attribute_infos)) return true;
  // Editor 形态：data 为 [{ product_type_id, attribute_infos }]
  if (Array.isArray(attrsResult.data)) {
    const first = attrsResult.data[0];
    if (first && typeof first === 'object' && (
      Array.isArray(first.attribute_infos)
      || Array.isArray(first.attributeInfos)
      || first.attribute_id != null
      || first.attributeId != null
    )) {
      return true;
    }
  }
  return false;
}

function attrRowId(row) {
  if (!row || typeof row !== 'object') return '';
  const id = row.id ?? row.attribute_id ?? row.attributeId;
  return id == null ? '' : String(id).trim();
}

/** 从后端 mapCategoryAttributes / OpenAPI raw 中抽出属性行 */
function collectTiktokAttrRows(attrsResult) {
  if (!attrsResult || typeof attrsResult !== 'object') return { rows: [], saleIds: new Set() };

  const byId = new Map();
  const saleIds = new Set();

  const pushRows = (list, { forceSale = false } = {}) => {
    (Array.isArray(list) ? list : []).forEach((row) => {
      if (!row || typeof row !== 'object') return;
      const id = attrRowId(row);
      if (!id) return;
      if (forceSale) saleIds.add(id);
      const prev = byId.get(id);
      if (!prev) {
        byId.set(id, row);
        return;
      }
      // 后出现的若带更多 values，优先保留更完整的一行
      const prevVals = Array.isArray(prev.values) ? prev.values.length : 0;
      const nextVals = Array.isArray(row.values) ? row.values.length : 0;
      if (nextVals > prevVals) byId.set(id, row);
    });
  };

  // 后端已拆好的销售列表优先记入 saleIds，再与全量 productAttrs 合并
  pushRows(attrsResult.skcSaleAttrs, { forceSale: true });
  pushRows(attrsResult.skuSaleAttrs, { forceSale: true });
  pushRows(attrsResult.productAttrs);
  pushRows(attrsResult.requiredProduct);
  pushRows(attrsResult.optionalProduct);

  if (byId.size) {
    return { rows: Array.from(byId.values()), saleIds };
  }

  const data = attrsResult.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (Array.isArray(data.attributes) && data.attributes.length) {
      pushRows(data.attributes);
      return { rows: Array.from(byId.values()), saleIds };
    }
    if (Array.isArray(data.category_attributes) && data.category_attributes.length) {
      pushRows(data.category_attributes);
      return { rows: Array.from(byId.values()), saleIds };
    }
  }

  if (Array.isArray(attrsResult.attributes) && attrsResult.attributes.length) {
    pushRows(attrsResult.attributes);
    return { rows: Array.from(byId.values()), saleIds };
  }

  return { rows: [], saleIds };
}

/** OpenAPI type：SALES_PROPERTY；兼容数字 1 / "1" / 含 SALE|SKC|SKU */
export function isTiktokSaleAttributeType(type) {
  if (type == null || type === '') return false;
  if (typeof type === 'number') return Number(type) === 1;
  const t = String(type).trim().toUpperCase();
  if (t === '1') return true;
  return t.includes('SALE') || t.includes('SKC') || t.includes('SKU');
}

function isSaleByName(name) {
  return /颜色|colour|\bcolor\b|尺码|尺寸|\bsize\b/i.test(String(name || ''));
}

function isColorByName(name) {
  return /颜色|colour|\bcolor\b/i.test(String(name || ''));
}

function isAttrRequired(p) {
  return p.required === true
    || p.is_required === true
    || p.is_requried === true // 平台文档拼写错误
    || Number(p.attribute_status ?? p.attributeStatus) === 3;
}

function extractLocalizedText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (typeof value === 'object') {
    const nested = value.zh
      ?? value['zh-CN']
      ?? value.zh_cn
      ?? value.en
      ?? value['en-US']
      ?? value.en_us
      ?? value.name
      ?? value.value
      ?? value.text;
    if (nested != null && nested !== value) return extractLocalizedText(nested);
  }
  return '';
}

function extractAttrValues(p) {
  if (!p || typeof p !== 'object') return [];
  if (Array.isArray(p.values)) return p.values;
  if (Array.isArray(p.attribute_value_info_list)) return p.attribute_value_info_list;
  if (Array.isArray(p.attributeValueInfoList)) return p.attributeValueInfoList;
  if (Array.isArray(p.value_list)) return p.value_list;
  if (Array.isArray(p.attribute_values)) return p.attribute_values;
  if (Array.isArray(p.attributeValues)) return p.attributeValues;
  return [];
}

function extractValueId(v) {
  if (!v || typeof v !== 'object') return '';
  const vid = v.id
    ?? v.attribute_value_id
    ?? v.attributeValueId
    ?? v.vid
    ?? v.value_id
    ?? v.valueId;
  return vid == null ? '' : String(vid).trim();
}

function extractValueName(v) {
  if (!v || typeof v !== 'object') return '';
  return extractLocalizedText(
    v.name
    ?? v.value
    ?? v.attribute_value
    ?? v.attribute_value_name
    ?? v.attributeValueName
    ?? v.attribute_value_cn
    ?? v.label,
  );
}

/** OpenAPI / 后端 mapped 行 → attribute_infos 项 */
function mapTiktokOpenApiAttrToEditorInfo(p, saleIdSet = new Set()) {
  if (!p || typeof p !== 'object') return null;
  const attributeId = p.id ?? p.attribute_id ?? p.attributeId;
  if (attributeId == null || String(attributeId).trim() === '') return null;

  const typeRaw = p.type ?? p.attribute_type ?? p.attributeType;
  const attrName = extractLocalizedText(p.name || p.attribute_name || p.attribute_name_cn || '');
  const idKey = String(attributeId).trim();
  const isSale = saleIdSet.has(idKey)
    || isTiktokSaleAttributeType(typeRaw)
    || isSaleByName(attrName);
  const required = isAttrRequired(p);
  const multi = p.is_multiple_selection === true || p.isMultipleSelection === true;
  const customizable = p.is_customizable === true || p.isCustomizable === true;
  const valuesRaw = extractAttrValues(p);

  let mode = 3;
  if (customizable && !valuesRaw.length) mode = 0;
  else if (multi) mode = 1;

  const values = valuesRaw.map((v) => {
    if (!v || typeof v !== 'object') return null;
    const vid = extractValueId(v);
    if (!vid) return null;
    const name = extractValueName(v);
    return {
      attribute_value_id: vid,
      attribute_value: name || vid,
      attribute_value_name: name || vid,
      attribute_value_en: extractLocalizedText(v.attribute_value_en || v.name_en || '') || '',
      tiktok_vid: vid,
      icon_url: v.icon_url || v.iconUrl || '',
    };
  }).filter(Boolean);

  return {
    attribute_id: attributeId,
    attribute_name: attrName || String(p.attribute_name || ''),
    attribute_name_cn: attrName || String(p.attribute_name_cn || p.attribute_name || ''),
    attribute_name_en: extractLocalizedText(p.attribute_name_en || '') || '',
    attribute_type: isSale ? 1 : 3,
    attribute_label: isSale && isColorByName(attrName) ? 1 : 0,
    attribute_status: required ? 3 : 0,
    attribute_mode: mode,
    attribute_input_num: multi ? 20 : 1,
    attribute_value_info_list: values,
    is_multiple_selection: multi,
    is_customizable: customizable,
    requirement_conditions: p.requirement_conditions || p.requirementConditions || [],
    tiktok_prop: {
      id: attributeId,
      name: attrName || String(p.attribute_name || ''),
      type: typeRaw,
      required,
      is_multiple_selection: multi,
      is_customizable: customizable,
      value_data_format: p.value_data_format || p.valueDataFormat || '',
      requirement_conditions: p.requirement_conditions || p.requirementConditions || [],
    },
  };
}

/** Temu properties 形态（历史兼容） */
function mapTemuPropertiesToEditorInfos(properties) {
  return properties.map((p) => {
    if (!p || typeof p !== 'object') return null;
    const templatePid = p.templatePid ?? p.template_pid;
    const pid = p.pid;
    const attributeId = templatePid ?? pid;
    if (attributeId == null) return null;
    const isSale = Boolean(p.isSale);
    const values = (Array.isArray(p.values) ? p.values : []).map((v) => ({
      attribute_value_id: v.vid ?? v.specId ?? v.id,
      attribute_value: v.value ?? v.specName ?? '',
      attribute_value_name: v.value ?? v.specName ?? '',
      attribute_value_en: v.lang2Value?.en || '',
      tiktok_vid: v.vid,
      tiktok_spec_id: v.specId,
      tiktok_value_group_id: v.group?.id,
      tiktok_value_group_name: v.group?.name,
    })).filter((v) => v.attribute_value_id != null && String(v.attribute_value || '').trim());

    return {
      attribute_id: attributeId,
      attribute_name: p.name || '',
      attribute_name_cn: p.name || '',
      attribute_name_en: p.lang2Name?.en || '',
      attribute_type: isSale ? 1 : 3,
      attribute_label: p.mainSale ? 1 : 0,
      attribute_status: p.required ? 3 : 0,
      attribute_mode: mapControlTypeToMode(p.controlType, p.chooseMaxNum),
      attribute_input_num: p.chooseMaxNum || p.inputMaxNum || 0,
      attribute_value_info_list: values,
      tiktok_prop: {
        templatePid,
        pid,
        refPid: p.refPid,
        name: p.name,
        isSale,
        mainSale: Boolean(p.mainSale),
        parentSpecId: p.parentSpecId,
        valueUnit: Array.isArray(p.valueUnit) ? (p.valueUnit[0] || '') : (p.valueUnit || ''),
        controlType: p.controlType,
        required: Boolean(p.required),
        showCondition: p.showCondition,
        parentTemplatePid: p.parentTemplatePid,
        templatePropertyValueParentList: p.templatePropertyValueParentList,
      },
    };
  }).filter(Boolean);
}

/**
 * @param {object} attrsResult TikTok attrs result（后端 publish/attrs 或 OpenAPI raw）
 * @param {{ catId?: string|number }} [opts]
 */
export function normalizeTiktokAttrsToEditorShape(attrsResult, opts = {}) {
  if (!attrsResult || typeof attrsResult !== 'object') {
    return { data: [], inputMaxSpecNum: 0 };
  }
  // 已是 Editor 形态则透传
  if (isEditorShape(attrsResult)) {
    return attrsResult;
  }

  const catId = opts.catId ?? attrsResult.catId ?? '';

  // 1) TikTok OpenAPI / 后端 mapCategoryAttributes
  const { rows: tiktokRows, saleIds } = collectTiktokAttrRows(attrsResult);
  if (tiktokRows.length) {
    const attribute_infos = tiktokRows
      .map((row) => mapTiktokOpenApiAttrToEditorInfo(row, saleIds))
      .filter(Boolean);
    return {
      data: [{
        product_type_id: catId ? Number(catId) : undefined,
        attribute_infos,
      }],
      inputMaxSpecNum: Number(attrsResult.inputMaxSpecNum || 0),
      productAttrs: attrsResult.productAttrs,
      skcSaleAttrs: attrsResult.skcSaleAttrs,
      skuSaleAttrs: attrsResult.skuSaleAttrs,
      _tiktokRaw: attrsResult,
    };
  }

  // 2) Temu properties 兼容
  const properties = Array.isArray(attrsResult.properties) ? attrsResult.properties : [];
  if (properties.length) {
    const attribute_infos = mapTemuPropertiesToEditorInfos(properties);
    return {
      data: [{
        product_type_id: catId ? Number(catId) : undefined,
        attribute_infos,
      }],
      inputMaxSpecNum: Number(attrsResult.inputMaxSpecNum || 0),
      properties,
      _tiktokRaw: attrsResult,
    };
  }

  return {
    data: [{
      product_type_id: catId ? Number(catId) : undefined,
      attribute_infos: [],
    }],
    inputMaxSpecNum: Number(attrsResult.inputMaxSpecNum || 0),
    _tiktokRaw: attrsResult,
  };
}
